import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, config, Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { encodeFunctionData, formatEther, formatUnits, getAddress, isAddress } from 'viem';
import Web3 from 'web3';
import { AccountState, IApiResponse, walletAccount } from '../shared/interface/interface';
import { AccountService } from './account.service';
import { publicClientConfig } from './publicClientConfig.service';
import { StorageService } from './storage.service';
import { AppkitService } from '../core/services/appkit.service';
const accessAbi = require('../common/abis/access-token.json');

const apiUrl = environment.API_BASE_URL;
const apiUrlV3 = environment.API_BASE_URL_V3;

const MULTICALL3_ADDRESS = '0xcA11bde05977b3631167028862bE2a173976CA11' as const;

// decimals() never changes for a deployed token, so it is read once per
// chain+token instead of on every batched balance call.
const tokenDecimalsCache = new Map<string, number>();

const multicall3Abi = [
    {
        inputs: [{ name: 'addr', type: 'address' }],
        name: 'getEthBalance',
        outputs: [{ name: 'balance', type: 'uint256' }],
        stateMutability: 'view',
        type: 'function'
    }
] as const;

const erc20Abi = [
    {
        inputs: [{ name: 'account', type: 'address' }],
        name: 'balanceOf',
        outputs: [{ name: '', type: 'uint256' }],
        stateMutability: 'view',
        type: 'function'
    },
    {
        inputs: [],
        name: 'decimals',
        outputs: [{ name: '', type: 'uint8' }],
        stateMutability: 'view',
        type: 'function'
    }
] as const;
@Injectable({
    providedIn: 'root'
})
export class UserManagementService {
    public blockStatus = new BehaviorSubject(false);
    public blockStatusObser: Observable<boolean>;
    // Short-lived in-memory caches so switching profile tabs does not re-show
    // the loading shimmer for the shared profile/balance shell on every switch.
    private userDetailsCache = new Map<string, any>();
    private balanceStateCache = new Map<string, { regulatedBalance: number; currencies: any[] }>();
    public contract: any;
    // public accessContract: any;
    public account: AccountState;
    public web3: any;
    private publicClient: any;
    constructor(
        private http: HttpClient,
        private storageService: StorageService,
        private accountService: AccountService,
        public publicClientConfig: publicClientConfig,
        private appkitService: AppkitService
    ) {
        this.blockStatusObser = this.blockStatus.asObservable();
        this.onInit();
    }

    public onInit() {

        this.account = this.storageService.getItem('wagmi_new_store') === null ?
            { address: '', chainId: '', isConnected: '' } :
            JSON.parse(this.storageService.getItem('wagmi_new_store') as any);

        setTimeout(() => {
            this.publicClient = this.publicClientConfig.publicClient(this.account.chainId);
        }, 200);
        // this.web3 = new Web3(window['ethereum'] as any || environment[this.account.chainId].PROVIDER);
        // setTimeout(() => {
        //     this.accessContract = new this.web3.eth.Contract(accessAbi, environment[this.account.chainId].ACCESS_CONTROL_TOKEN);
        // }, 200);
    }

    /**
  * Gets all users
  * @param {number} page
  * @param {number} limit
  * @param {boolean} isBlocked
  * @param {string} item
  * @returns
  */
    public getAllUsers(page: number, limit: number, isBlocked: boolean, item: string) {
        return this.http.get(`${apiUrlV3}management/users?blocked=${isBlocked}&page=${page}&limit=${limit}${item}`);
    }

    /**
     * Exports all users matching the current filters (no pagination).
     * @param {boolean} isBlocked
     * @param {string} extraQuery  e.g. `&item=foo&kyc=verified`
     */
    public exportAllUsers(isBlocked: boolean, extraQuery: string = '') {
        return this.http.get(`${apiUrlV3}management/users/export?blocked=${isBlocked}${extraQuery}`);
    }

    /**
     * Gets every "Manage users" metric card in one call: user / holdings / KYC
     * counts from the database and the native + token balance totalled on chain
     * over every wallet of the current custody type. Deliberately takes no
     * filters - the cards always describe the whole custody base.
     */
    public getUsersSummary() {
        return this.http.get(`${apiUrlV3}management/users/summary`);
    }

    /**
     * Updates users
     * @param {string} userId
     * @param {string} wallet_address
     * @param {object} data
     * @returns
     */
    public updateUsers(data: object, userId: string, wallet_address: string) {
        return this.http.patch(`${apiUrlV3}management/user-update?id=${userId}`, data);
    }

    /**
     * Blocks user
     * @param {string} id
     * @param {boolean} status
     * @param {any} txData
     */
    public blockUser(id: string, status: boolean, txData?: any) {
        const data = {
            is_blocked: status,
            transaction_hash: txData?.transactionHash,
            gas_price: formatEther(txData?.effectiveGasPrice.toString()),
            block_number: `${txData?.blockNumber}`
        };
        return this.http.patch(`${apiUrlV3}management/manage-user-access?id=${id}`, data);
    }

    /**
     * Delete user
     * @param {string} id
     * @param {string} wallet_address
     * @returns
     */
    public deleteUser(id: string, wallet_address: string) {
        return this.http.delete(`${apiUrlV3}management/user-delete?id=${id}&admin_address=${wallet_address}`);
    }

    /**
    * Block or ubBlock user contract
    * @param {string} address
    */
    public async blockUnblockUser(address: string, status: boolean, walletAddress: string) {
        let requiredGas;
        let blockUnblockAbi;
        if (status) {
            const contractParams = {
                address: environment[this.account.chainId].ACCESS_CONTROL_TOKEN,
                abi: accessAbi,
                functionName: 'blockUser',
                args: [address]
            };
            requiredGas = await this.publicClient.estimateContractGas({
                account: walletAddress,
                ...contractParams
            });

            blockUnblockAbi = await encodeFunctionData({
                ...contractParams
            })

        }
        else {
            const contractParams = {
                address: environment[this.account.chainId].ACCESS_CONTROL_TOKEN,
                abi: accessAbi,
                functionName: 'unblockUser',
                args: [address]
            };
            requiredGas = await this.publicClient.estimateContractGas({
                account: walletAddress,
                ...contractParams
            });

            blockUnblockAbi = await encodeFunctionData({
                ...contractParams
            })

        }

        requiredGas = requiredGas.toString();

        return { blockUnblockAbi, requiredGas }
    }

    /**
     * Get users
     * @param {string} id
     * @returns
     */
    public getUser(id: string) {
        return this.http.get(`${apiUrl}/user?id=${id}`);
    }

    /**
     * Gets user by address
     * @param {string} walletAddress
     * @returns
     */
    public getUserByAddress(walletAddress: string) {
        return this.http.get(`${apiUrlV3}management/get-user?wallet_address=${walletAddress}`)
            .pipe(tap((response) => this.userDetailsCache.set(walletAddress, response)));
    }

    /**
     * Returns the last cached get-user response for the given key (wallet
     * address or database id). Lets profile tabs render instantly on tab
     * switches instead of re-showing the loading shimmer while the fresh
     * request is in flight.
     * @param {string} key wallet address or database id used for the lookup
     * @returns cached response or undefined
     */
    public peekUser(key: string): any {
        return this.userDetailsCache.get(key);
    }

    /**
     * Returns the last cached wallet balance state for an address (or undefined).
     * @param {string} walletAddress
     * @returns cached { regulatedBalance, currencies } or undefined
     */
    public peekBalanceState(walletAddress: string): { regulatedBalance: number; currencies: any[] } | undefined {
        return this.balanceStateCache.get(walletAddress);
    }

    /**
     * Stores the latest wallet balance state for an address so it can be shown
     * immediately when the profile shell is re-mounted on a tab switch.
     * @param {string} walletAddress
     * @param {object} state balance snapshot to cache
     * @returns {void}
     */
    public setBalanceState(walletAddress: string, state: { regulatedBalance: number; currencies: any[] }): void {
        this.balanceStateCache.set(walletAddress, state);
    }

    /**
     * Gets user by database id
     * @param {string} id
     * @returns
     */
    public getUserById(id: string) {
        return this.http.get(`${apiUrlV3}management/get-user?id=${id}`)
            .pipe(tap((response) => this.userDetailsCache.set(id, response)));
    }

    /**
     * Get user nft
     * @param {string} address
     * @param {number}page
     * @param {number}limit
     * @returns
     */
    public getUserNft(address: string, page = 1, limit = 9): Observable<IApiResponse> {
        return this.http.get<IApiResponse>(`${apiUrlV3}management/nft-holdings-by-owner?address=${address}&page=${page}&limit=${limit}`);
    }

    /**
     * Updates block status
     * @param {boolean} status
     */
    public updateBlockStatus(status: boolean) {
        this.blockStatus.next(status);
    }

    /**
     * Profiles image upload
     * @param {File} file
     * @returns
     */
    profileImageUpload(file: File) {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('service_type', 'image');
        return this.http.post(`${apiUrlV3}file-upload/document`, formData)
    }

    /**
     * Updates kyc status
     * @param {string} id
     * @param {any} data
     */
    updateKycStatus(id: string, data: any) {
        return this.http.patch(`${apiUrlV3}management/kyc-enable-disable?id=${id}`, data);
    }

    /**
     * Gets the communities a user can be put in (active ones only)
     * @returns
     */
    getActiveCommunities(): Observable<IApiResponse> {
        return this.http.get<IApiResponse>(`${apiUrlV3}management/communities`);
    }

    /**
     * Adds a user to a community, moves them to another one, or removes them (communityId null)
     * @param {string} userId
     * @param {number | null} communityId
     * @returns
     */
    updateUserCommunity(userId: string, communityId: number | null): Observable<IApiResponse> {
        return this.http.patch<IApiResponse>(`${apiUrlV3}management/user-community`, { community_id: communityId }, { params: { id: userId } });
    }

    /**
     * Adds, changes or removes a custodial user's withdrawal wallet (an empty address removes it)
     * @param {string} userId
     * @param {string} address
     * @returns {Observable<IApiResponse>}
     */
    updateUserWithdrawalWallet(userId: string, address: string): Observable<IApiResponse> {
        return this.http.patch<IApiResponse>(`${apiUrlV3}management/user-withdrawal-wallet`, { withdrawal_wallet_address: address }, { params: { id: userId } });
    }

    /**
     * Gets kyc status from admin
     * @returns
     */
    getAdminKycStatus(): Observable<IApiResponse> {
        return this.http.get<IApiResponse>(`${apiUrlV3}management/get-admin-kyc-status`);
    }

    /**
     * Gets currencies
     * @returns
     */
    getCurrencies() {
        return this.http.get(`${apiUrlV3}management/currencies`);
    }

    /**
     * Gets native balance
     * @param walletAddress
     * @param networkId
     * @returns
     */
    async getNativeBalance(walletAddress: any, networkId: any) {
        // strict:false + getAddress(): stored addresses are not always checksummed, and
        // viem rejects a mis-cased address when it abi-encodes it.
        if (!isAddress(String(walletAddress ?? ''), { strict: false })) {
            return { decimals: 18, formatted: '0', value: BigInt(0) };
        }
        const client = this.publicClientConfig.publicClient(networkId);
        const value = await client.getBalance({ address: getAddress(walletAddress) });
        return { decimals: 18, formatted: formatEther(value), value };
    }

    /**
     * Gets token balance
     * @param walletAddress
     * @param networkId
     * @param tokenAddress
     * @returns
     */
    async getTokenBalance(walletAddress: any, networkId: any, tokenAddress: any) {
        if (!isAddress(String(walletAddress ?? ''), { strict: false }) || !isAddress(String(tokenAddress ?? ''), { strict: false })) {
            return { decimals: 18, formatted: '0', value: BigInt(0) };
        }
        const client = this.publicClientConfig.publicClient(networkId);
        const token = getAddress(tokenAddress);
        const [value, decimals] = await Promise.all([
            client.readContract({ address: token, abi: erc20Abi, functionName: 'balanceOf', args: [getAddress(walletAddress)] }),
            client.readContract({ address: token, abi: erc20Abi, functionName: 'decimals' })
        ]);
        return { decimals: Number(decimals), formatted: formatUnits(value, Number(decimals)), value };
    }

    /**
     * Gets native balances for multiple wallet addresses in a single multicall RPC request.
     * @param {string[]} walletAddresses
     * @param {any} chainId
     * @returns {{ [address: string]: number }}
     */
    async getBatchNativeBalances(walletAddresses: string[], chainId: any): Promise<{ [address: string]: number }> {
        const addresses = walletAddresses.filter(address => isAddress(String(address ?? ''), { strict: false }));
        if (!addresses.length) return {};
        const client = this.publicClientConfig.publicClient(chainId);
        const contracts: any[] = addresses.map(address => ({
            address: MULTICALL3_ADDRESS,
            abi: multicall3Abi,
            functionName: 'getEthBalance',
            args: [getAddress(address)]
        }));
        const results: any[] = await (client as any).multicall({ contracts });
        const balances: { [address: string]: number } = {};
        results.forEach((result: any, index: number) => {
            balances[addresses[index]] = result.status === 'success'
                ? Number(formatEther(result.result))
                : 0;
        });
        return balances;
    }

    /**
     * Gets ERC20 token balances for multiple wallet addresses in a single multicall RPC request.
     * @param {string[]} walletAddresses
     * @param {any} chainId
     * @param {string} tokenAddress
     * @returns {{ [address: string]: number }}
     */
    async getBatchTokenBalances(walletAddresses: string[], chainId: any, tokenAddress: string): Promise<{ [address: string]: number }> {
        const addresses = walletAddresses.filter(address => isAddress(String(address ?? ''), { strict: false }));
        if (!addresses.length) return {};
        const client = this.publicClientConfig.publicClient(chainId);
        const decimalsCacheKey = `${chainId}:${getAddress(tokenAddress)}`;
        let decimals = tokenDecimalsCache.get(decimalsCacheKey);
        if (decimals === undefined) {
            const decimalsResult = await client.readContract({
                address: getAddress(tokenAddress),
                abi: erc20Abi,
                functionName: 'decimals'
            });
            decimals = Number(decimalsResult);
            tokenDecimalsCache.set(decimalsCacheKey, decimals);
        }
        const contracts: any[] = addresses.map(address => ({
            address: getAddress(tokenAddress),
            abi: erc20Abi,
            functionName: 'balanceOf',
            args: [getAddress(address)]
        }));
        const results: any[] = await (client as any).multicall({ contracts });
        const balances: { [address: string]: number } = {};
        results.forEach((result: any, index: number) => {
            balances[addresses[index]] = result.status === 'success'
                ? Number(formatUnits(result.result, decimals))
                : 0;
        });
        return balances;
    }

    /**
     * Updates the personal information of a user
     * 
     * @param {string} userId - The unique identifier of the user whose information is being updated.
     * @param {IPersonalInfo} personalInfo - An object containing the user's personal information to be updated.
     * @returns {Observable<any>} - An Observable that emits the response from the server.
     */
    updatePersonalInfo(userId: string, personalInfo: IPersonalInfo) {
        return this.http.patch(`${apiUrlV3}management/user-update`, personalInfo, { params: { id: userId } }
        );
    }

    /**
     * Gets user login activity
     * @param userId 
     * @returns  
     */
    getUserLoginActivity(userId: string, from_date: string = '', to_date: string = '', from_time: string = '', to_time = '', page: number = 1, limit: number = 10, search: string = '', sortBy: string = '', sortOrder: 'asc' | 'desc' = 'desc') {
        const sortParams = sortBy ? `&sort_by=${sortBy}&sort_order=${sortOrder}` : '';
        return this.http.get(`${environment.API_BASE_URL_V3}management/user-login-details?user_id=${userId}&from_date=${from_date}&from_time=${from_time}&to_date=${to_date}&to_time=${to_time}&page=${page}&limit=${limit}&search=${encodeURIComponent(search)}${sortParams}`)
    }


    /**
     * Gets loan history
     * @param walletAddress 
     * @returns  
     */
    public getLoanHistry(page: number, limit: number, userId: string, search: string = '', sortBy: string = '', sortOrder: 'asc' | 'desc' = 'desc', filters: { fromDate?: string; toDate?: string; method?: string; includeMethodOptions?: boolean } = {}) {
        const searchParam = search ? `&search=${encodeURIComponent(search)}` : '';
        const sortParams = sortBy ? `&sort_by=${sortBy}&sort_order=${sortOrder}` : '';
        const filterParams = (filters.fromDate ? `&from_date=${filters.fromDate}` : '')
            + (filters.toDate ? `&to_date=${filters.toDate}` : '')
            + (filters.method ? `&method=${encodeURIComponent(filters.method)}` : '')
            + (filters.includeMethodOptions ? '&include_method_options=true' : '');
        return this.http.get(`${apiUrlV3}management/get-user-loan-logs?id=${userId}&page=${page}&limit=${limit}${searchParam}${sortParams}${filterParams}`);
    }

    /**
     * Gets kyc history
     * @param userId 
     * @returns  
     */
    public getKycHistory(userId: string, page: number = 1, limit: number = 10, search: string = '', sortBy: string = '', sortOrder: 'asc' | 'desc' = 'desc') {
        const searchParam = search ? `&search=${encodeURIComponent(search)}` : '';
        const sortParams = sortBy ? `&sort_by=${sortBy}&sort_order=${sortOrder}` : '';
        return this.http.get(`${apiUrlV3}management/get-user-kyc-history?id=${userId}&page=${page}&limit=${limit}${searchParam}${sortParams}`);
    }

    /**
     * Gets KYC documents
     * @param userId 
     * @returns 
     */
    public getKycDocuments(userId: string) {
        return this.http.get(`${apiUrlV3}management/idenfy/documents`, { params: { id: userId } });
    }

    /**
     * Gets active loans
     * @param walletAddress 
     * @returns  
     */
    public getActiveLoans(userId: string, page = 1, limit = 9, type: string, search: string = '', sortBy: string = '', sortOrder: 'asc' | 'desc' = 'desc', filters: UserLoanFilters = {}): Observable<IApiResponse> {
        const searchParam = search ? `&search=${encodeURIComponent(search)}` : '';
        const sortParams = sortBy ? `&sort_by=${sortBy}&sort_order=${sortOrder}` : '';
        return this.http.get<IApiResponse>(`${apiUrlV3}management/get-user-loans?id=${userId}&page=${page}&limit=${limit}&type=${type}${searchParam}${sortParams}${filterParams(filters)}`);
    }

    /**
     * Sends welcome email
     * @param userId 
     * @returns welcome email 
     */
    sendWelcomeEmail(userId:any):Observable<any>{
        return this.http.get(`${apiUrlV3}management/nft/send-welcome-email-to-owner?id=${userId}`)
    }

    /**
     * Creates a user referral.
     * @param payload The referral data.
     * @returns An observable of the API response.
     */
    public createUserReferral(payload: any): Observable<any> {
        return this.http.post(`${apiUrlV3}management/create-user-referral`, payload);
    }

    /**
     * Gets user referral list
     * @param userId 
     * @returns  
     */
    public getUserReferralList(userId: string): Observable<any> {
        return this.http.get(`${apiUrlV3}management/get-user-referral-list?id=${userId}`);
    }

    /**
     * Updates a user referral.
     * @param referralId The ID of the referral to update.
     * @param payload The updated referral data.
     * @returns An observable of the API response.
     */
    public updateUserReferral(referralId: string, payload: any): Observable<any> {
        return this.http.patch(`${apiUrlV3}management/update-user-referral?id=${referralId}`, payload, {
            headers: { 'regulated': 'true', 'Content-Type': 'application/json' }
        });
    }

    /**
     * Deletes a user referral.
     * @param referralId The ID of the referral to delete.
     * @returns An observable of the API response.
     */
    public deleteUserReferral(referralId: string): Observable<any> {
        return this.http.delete(`${apiUrlV3}management/delete-user-referral?id=${referralId}`);
    }

    /**
     * Gets negotiation
     * @param page 
      * @param limit 
      * @param walletAddress 
      * @returns  
      */
     public getNegotiation(page: number, limit: number, id: string, sortBy: string = '', sortOrder: 'asc' | 'desc' = 'desc', filters: UserLoanFilters = {}):Observable<any> {
    const sortParams = sortBy ? `&sort_by=${sortBy}&sort_order=${sortOrder}` : '';
    return this.http.get(`${apiUrlV3}management/get-user-loan-negotiations?id=${id}&page=${page}&limit=${limit}${sortParams}${filterParams(filters)}`);
  }

}

/**
 * Search and filters for a user's Loans tab, sent only when set so a plain request is unchanged.
 * Loan cards read role / status / end_from / end_to (maturity); Negotiation reads role / status /
 * type / from_date / to_date (last action).
 */
export interface UserLoanFilters {
    search?: string;
    role?: string;
    status?: string;
    type?: string;
    end_from?: string;
    end_to?: string;
    from_date?: string;
    to_date?: string;
}

const filterParams = (filters: UserLoanFilters): string =>
    Object.entries(filters)
        .filter(([, value]) => value !== undefined && value !== null && String(value).trim() !== '')
        .map(([key, value]) => `&${key}=${encodeURIComponent(String(value).trim())}`)
        .join('');

export interface IPersonalInfo {
    first_name?: string,
    last_name?: string,
    notes?: string,
    email?: string,
    phone_number?: any,
    display_name?: string,
    is_policy_accepted?:boolean
}
